import React, { useState, useCallback, useEffect } from 'react';
import KPICard from '../../components/Dashboard/KPICard/KPICard';
import LineChart from '../../components/Dashboard/LineChart/LineChart';
import StackedBarChart from '../../components/Dashboard/StackedBarChart/StackedBarChart';
import BarChart from '../../components/Dashboard/BarChart/BarChart';
import PieChart from '../../components/Dashboard/PieChart/PieChart';
import { fetchApi } from '../../../helpers/fetch';
import './Admin.scss';

const Engagement = () => {
    const [dateRange, setDateRange] = useState('7days');
    const [loading, setLoading] = useState(false);
    
    // Default/initial data structure
    const [engagementData, setEngagementData] = useState({
        // Engagement Section - Implemented (from API)
        totalUsers: { value: 0, trend: null, trendLabel: 'since last week' },
        payingUsers: { value: 0, trend: null, trendLabel: 'since last week' },
        arppu: { value: 0, trend: null, trendLabel: 'since last week' },
        buyersOnUsersRate: { value: 0, trend: null, trendLabel: 'since last week' },
        repeatPurchaseRate: { value: 0, trend: null, trendLabel: 'since last week' },
        contributorsOnUsersRate: { value: 0, trend: null, trendLabel: 'since last week' },
        repeatContributionRate: { value: 0, trend: null, trendLabel: 'since last week' },
        avgGmvPerUser: { value: 0, trend: null, trendLabel: 'since last week' },
        purchaseFrequencyByTier: [],
        userToCreatorRatio: { value: '0 to 1', trend: null, trendLabel: 'since last week' },
        purchaseFrequency: { value: 0, trend: null, trendLabel: 'since last week' },
        abandonedCarts: { value: 0, trend: null, trendLabel: 'since last week' },
        
        // Blocked fields (dummy data, not from API)
        activeUsers30Days: 390,
        dailyActiveUsers: 241,
        weeklyActiveUsers: 391,
        monthlyActiveUsers: 454,
        uniqueVisits: 132,
        accountsCreated: 68,
        emailsClickRate: [
            { name: 'Clicked emails', value: 58 },
            { name: 'Opened only emails', value: 42 }
        ],
        conversionRate: 59,
        emailsOpeningRate: 59,
        newUsersPerChannel: [
            { date: '10/11', 'Website (GA4)': 45, 'Social media': 30, 'Invites (Dashboard)': 15, 'Email (SendGrid)': 10 },
            { date: '11/11', 'Website (GA4)': 50, 'Social media': 35, 'Invites (Dashboard)': 18, 'Email (SendGrid)': 12 },
            { date: '12/11', 'Website (GA4)': 48, 'Social media': 32, 'Invites (Dashboard)': 16, 'Email (SendGrid)': 11 },
            { date: '13/11', 'Website (GA4)': 52, 'Social media': 38, 'Invites (Dashboard)': 20, 'Email (SendGrid)': 13 },
            { date: '14/11', 'Website (GA4)': 55, 'Social media': 40, 'Invites (Dashboard)': 22, 'Email (SendGrid)': 15 },
            { date: '15/11', 'Website (GA4)': 53, 'Social media': 37, 'Invites (Dashboard)': 19, 'Email (SendGrid)': 14 },
            { date: '16/11', 'Website (GA4)': 57, 'Social media': 42, 'Invites (Dashboard)': 24, 'Email (SendGrid)': 16 }
        ],
        newUsersPerChannelTotal: 258,
        kFactor: 274,
        supportersActivationRate: 32,
        profilesCompletionRate: 57,
        sessionDuration: 10
    });

    const formatCurrency = useCallback((amount) => {
        return `€${(amount / 100).toFixed(2)}`;
    }, []);

    const formatPercentage = useCallback((value) => {
        return `${value}%`;
    }, []);

    // Helper function to extract value and trend from API response
    const extractValueAndTrend = useCallback((data) => {
        if (data && typeof data === 'object' && 'value' in data) {
            // New format with trend data
            const trendValue = data.trend ? parseFloat(data.trend.replace(/[+%]/g, '')) : null;
            return {
                value: data.value,
                trend: trendValue,
                trendLabel: data.trendLabel || 'since last week'
            };
        }
        // Old format or simple value
        return {
            value: data || 0,
            trend: null,
            trendLabel: 'since last week'
        };
    }, []);

    const fetchEngagementData = useCallback(async () => {
        try {
            setLoading(true);
            
            const response = await fetchApi(`/admin/engagement?period=${dateRange}`);
            
            console.log('/admin/engagement response:', response);
            
            if (response.success && response.data) {
                setEngagementData({
                    ...response.data,
                    // Keep blocked fields as dummy data (not from API)
                    activeUsers30Days: 390,
                    dailyActiveUsers: 241,
                    weeklyActiveUsers: 391,
                    monthlyActiveUsers: 454,
                    uniqueVisits: 132,
                    accountsCreated: 68,
                    emailsClickRate: [
                        { name: 'Clicked emails', value: 58 },
                        { name: 'Opened only emails', value: 42 }
                    ],
                    conversionRate: 59,
                    emailsOpeningRate: 59,
                    newUsersPerChannel: [
                        { date: '10/11', 'Website (GA4)': 45, 'Social media': 30, 'Invites (Dashboard)': 15, 'Email (SendGrid)': 10 },
                        { date: '11/11', 'Website (GA4)': 50, 'Social media': 35, 'Invites (Dashboard)': 18, 'Email (SendGrid)': 12 },
                        { date: '12/11', 'Website (GA4)': 48, 'Social media': 32, 'Invites (Dashboard)': 16, 'Email (SendGrid)': 11 },
                        { date: '13/11', 'Website (GA4)': 52, 'Social media': 38, 'Invites (Dashboard)': 20, 'Email (SendGrid)': 13 },
                        { date: '14/11', 'Website (GA4)': 55, 'Social media': 40, 'Invites (Dashboard)': 22, 'Email (SendGrid)': 15 },
                        { date: '15/11', 'Website (GA4)': 53, 'Social media': 37, 'Invites (Dashboard)': 19, 'Email (SendGrid)': 14 },
                        { date: '16/11', 'Website (GA4)': 57, 'Social media': 42, 'Invites (Dashboard)': 24, 'Email (SendGrid)': 16 }
                    ],
                    newUsersPerChannelTotal: 258,
                    kFactor: 274,
                    supportersActivationRate: 32,
                    profilesCompletionRate: 57,
                    sessionDuration: 10
                });
            } else {
                console.warn('API response not successful:', response);
            }
        } catch (error) {
            console.error('Error fetching engagement data:', error);
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        fetchEngagementData();
    }, [fetchEngagementData]);

    return (
        <div className="engagement-dashboard">
            <div className="dashboard-header">
                <div className="header-controls">
                    <select 
                        value={dateRange} 
                        onChange={(e) => setDateRange(e.target.value)}
                        className="date-range-select"
                    >
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                        <option value="90days">Last 90 days</option>
                        <option value="1year">Last year</option>
                    </select>
                    <button className="export-btn">Export</button>
                </div>
            </div>

            {/* Engagement Section */}
            <div className="dashboard-section">
                <div className="section-header">
                    <h2>Engagement</h2>
                </div>
                
                <div className="engagement-kpis-grid">
                    {(() => {
                        const totalUsers = extractValueAndTrend(engagementData.totalUsers);
                        return (
                            <KPICard
                                title="Total Users"
                                value={totalUsers.value}
                                trend={totalUsers.trend}
                                trendLabel={totalUsers.trendLabel}
                                color={totalUsers.trend && totalUsers.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    <div className="blocked-kpi-wrapper" style={{ position: 'relative' }}>
                        <KPICard
                            title="Active Users (30 days)"
                            value={engagementData.activeUsers30Days}
                            trend={5}
                            trendLabel="since last 30days"
                            color="#4caf50"
                            loading={loading}
                        />
                    </div>
                    {(() => {
                        const payingUsers = extractValueAndTrend(engagementData.payingUsers);
                        return (
                            <KPICard
                                title="Paying users"
                                value={payingUsers.value}
                                trend={payingUsers.trend}
                                trendLabel={payingUsers.trendLabel}
                                color={payingUsers.trend && payingUsers.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const arppu = extractValueAndTrend(engagementData.arppu);
                        return (
                            <KPICard
                                title="ARPPU"
                                value={formatCurrency(arppu.value)}
                                trend={arppu.trend}
                                trendLabel={arppu.trendLabel}
                                color={arppu.trend && arppu.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                </div>

                <div className="engagement-kpis-grid">
                    {(() => {
                        const buyersRate = extractValueAndTrend(engagementData.buyersOnUsersRate);
                        return (
                            <KPICard
                                title="Buyers on users rate"
                                value={formatPercentage(buyersRate.value)}
                                trend={buyersRate.trend}
                                trendLabel={buyersRate.trendLabel}
                                color={buyersRate.trend && buyersRate.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const repeatPurchase = extractValueAndTrend(engagementData.repeatPurchaseRate);
                        return (
                            <KPICard
                                title="Repeat Purchase rate"
                                value={formatPercentage(repeatPurchase.value)}
                                trend={repeatPurchase.trend}
                                trendLabel={repeatPurchase.trendLabel}
                                color={repeatPurchase.trend && repeatPurchase.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const contributorsRate = extractValueAndTrend(engagementData.contributorsOnUsersRate);
                        return (
                            <KPICard
                                title="Contributors on users rate"
                                value={formatPercentage(contributorsRate.value)}
                                trend={contributorsRate.trend}
                                trendLabel={contributorsRate.trendLabel}
                                color={contributorsRate.trend && contributorsRate.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const repeatContribution = extractValueAndTrend(engagementData.repeatContributionRate);
                        return (
                            <KPICard
                                title="Repeat Contribution rate"
                                value={formatPercentage(repeatContribution.value)}
                                trend={repeatContribution.trend}
                                trendLabel={repeatContribution.trendLabel}
                                color={repeatContribution.trend && repeatContribution.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                </div>

                <div className="chart-card" style={{ position: 'relative' }}>
                    <div className="not-implemented-overlay">
                        <div className="not-implemented-message">
                            <span className="not-implemented-icon">🚧</span>
                            <h3>Not Implemented Yet</h3>
                            <p>This section is currently using dummy data. Implementation coming soon.</p>
                        </div>
                    </div>
                    <div className="chart-kpi">
                        <span className="kpi-label">Purchase frequency by creator tier</span>
                    </div>
                    <BarChart
                        data={engagementData.purchaseFrequencyByTier}
                        xAxisKey="tier"
                        dataKey="value"
                        color="#42a5f5"
                        height={300}
                        loading={loading}
                    />
                </div>

                <div className="engagement-kpis-grid">
                    {(() => {
                        const avgGmv = extractValueAndTrend(engagementData.avgGmvPerUser);
                        return (
                            <KPICard
                                title="Avg. GMV per User"
                                value={formatCurrency(avgGmv.value)}
                                trend={avgGmv.trend}
                                trendLabel={avgGmv.trendLabel}
                                color={avgGmv.trend && avgGmv.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const userToCreator = extractValueAndTrend(engagementData.userToCreatorRatio);
                        return (
                            <KPICard
                                title="User-to-Creator ratio"
                                value={userToCreator.value}
                                trend={userToCreator.trend}
                                trendLabel={userToCreator.trendLabel}
                                color={userToCreator.trend && userToCreator.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const purchaseFreq = extractValueAndTrend(engagementData.purchaseFrequency);
                        return (
                            <KPICard
                                title="Purchase frequency"
                                value={purchaseFreq.value}
                                trend={purchaseFreq.trend}
                                trendLabel={purchaseFreq.trendLabel}
                                color={purchaseFreq.trend && purchaseFreq.trend >= 0 ? "#4caf50" : "#f44336"}
                                loading={loading}
                            />
                        );
                    })()}
                    {(() => {
                        const abandonedCarts = extractValueAndTrend(engagementData.abandonedCarts);
                        return (
                            <KPICard
                                title="Abandoned carts"
                                value={abandonedCarts.value}
                                trend={abandonedCarts.trend}
                                trendLabel={abandonedCarts.trendLabel}
                                color={abandonedCarts.trend && abandonedCarts.trend >= 0 ? "#f44336" : "#4caf50"}
                                loading={loading}
                            />
                        );
                    })()}
                </div>

                <div className="engagement-kpis-grid blocked-section">
                    
                    <KPICard
                        title="Daily Active Users"
                        value={engagementData.dailyActiveUsers}
                        trend={5}
                        trendLabel="since yesterday"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Weekly Active Users"
                        value={engagementData.weeklyActiveUsers}
                        trend={17}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Monthly Active Users"
                        value={engagementData.monthlyActiveUsers}
                        trend={99}
                        trendLabel="since last month"
                        color="#4caf50"
                        loading={loading}
                    />
                </div>
            </div>

            {/* Acquisition & Activation Section */}
            <div className="dashboard-section blocked-section">
                <div className="section-header">
                    <h2>Acquisition & Activation</h2>
                </div>
              

                <div className="acquisition-kpis-grid">
                    <KPICard
                        title="Unique Visits on website"
                        value={engagementData.uniqueVisits}
                        trend={14}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Accounts created"
                        value={engagementData.accountsCreated}
                        trend={3.9}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <div className="chart-card">
                        <div className="chart-kpi">
                            <span className="kpi-label">Emails click rate (opened/clicked)</span>
                        </div>
                        <PieChart
                            data={engagementData.emailsClickRate}
                            dataKey="value"
                            nameKey="name"
                            height={250}
                            colors={['#42a5f5', '#ec407a']}
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="acquisition-kpis-grid">
                    <KPICard
                        title="Conversion rate"
                        value={formatPercentage(engagementData.conversionRate)}
                        trend={10}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Emails opening rate"
                        value={formatPercentage(engagementData.emailsOpeningRate)}
                        trend={10}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                </div>

                <div className="chart-card">
                    <div className="chart-kpi">
                        <span className="kpi-label">New users per channel</span>
                        <span className="kpi-value">{engagementData.newUsersPerChannelTotal}</span>
                        <span className="kpi-trend positive">+0 (0%) since last week</span>
                    </div>
                    <StackedBarChart
                        data={engagementData.newUsersPerChannel}
                        xAxisKey="date"
                        bars={[
                            { key: 'Website (GA4)', color: '#42a5f5', name: 'Website (GA4)' },
                            { key: 'Social media', color: '#ec407a', name: 'Social media' },
                            { key: 'Invites (Dashboard)', color: '#ab47bc', name: 'Invites (Dashboard)' },
                            { key: 'Email (SendGrid)', color: '#9c27b0', name: 'Email (SendGrid)' }
                        ]}
                        height={300}
                        loading={loading}
                    />
                </div>

                <div className="acquisition-kpis-grid">
                    <KPICard
                        title="K-Factor"
                        value={engagementData.kFactor}
                        trend={14}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Supporters activation rate"
                        value={formatPercentage(engagementData.supportersActivationRate)}
                        trend={3.9}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Profiles completion rate"
                        value={formatPercentage(engagementData.profilesCompletionRate)}
                        trend={6.7}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                    <KPICard
                        title="Session duration"
                        value={`${engagementData.sessionDuration}mins`}
                        trend={3.75}
                        trendLabel="since last week"
                        color="#4caf50"
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
};

export default Engagement;

